import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as db from '../utils/db';

const StoreContext = createContext();

export const useStore = () => useContext(StoreContext);

// API 베이스 URL 설정 (로컬 개발 및 AWS 배포 대응)
const getApiBaseUrl = () => {
    // 1. 환경 변수로 명시된 API 주소가 있으면 최우선 사용
    if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;

    // 2. 현재 도메인이 localhost면 3001 포트 강제 (개발 편의)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `http://${window.location.hostname}:3001/api`;
    }

    // 3. 그 외(AWS 배포 환경 등)는 현재 호스트 주소를 그대로 사용
    return `${window.location.origin}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// 헬퍼 함수: 자바스크립트 Date 객체를 현지 시간 기준 YYYY-MM-DD 형식으로 변환
export const getLocalISODate = (date = new Date()) => {
    const offset = date.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date - offset)).toISOString().split('T')[0];
    return localISOTime;
};

export const StoreProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const isInitialLoad = React.useRef(true);
    const isClearingData = React.useRef(false); // [핵심] 초기화 작업 중 동기화 레이스 컨디션 차단 락
    const [currentDate, setCurrentDate] = useState(getLocalISODate());
    const [stores, setStores] = useState([]);
    const [eventHistory, setEventHistory] = useState({});
    const [attendanceHistory, setAttendanceHistory] = useState({});
    const [user, setUser] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [backups, setBackups] = useState([]);
    const [syncStats, setSyncStats] = useState({ serverRecords: 0, localRecords: 0 });
    const [selectedStoreId, setSelectedStoreId] = useState('all');

    // 헬퍼 함수: 타임아웃이 적용된 fetch (31MB 대용량 데이터 및 모바일 환경 고려하여 기본 30초로 증설)
    const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(id);
            return response;
        } catch (error) {
            clearTimeout(id);
            throw error;
        }
    };

    // 접속/활동 로그 기록 함수
    const logActivity = useCallback(async (action, details = '', providedUser = null) => {
        const targetUser = providedUser || user;
        if (!targetUser || targetUser.role !== 'admin') return;
        
        try {
            await fetch(`${API_BASE_URL}/access-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: targetUser.id,
                    action,
                    details,
                    timestamp: new Date().toISOString()
                })
            });
        } catch (e) {
            console.error('Failed to log activity:', e);
        }
    }, [user, API_BASE_URL]);

    // 1. 초기 데이터 로드 및 서버 동기화
    useEffect(() => {
        const initData = async () => {
            try {
                // 1-1. 서버에서 최신 데이터 가져오기 시도 (타임아웃 적용)
                let serverStores, serverHistory, serverVendors;

                // [Fix] 1-1a. 로컬 데이터 로드 우선 실행 (Cannot access 'savedStores' before initialization 에러 방지)
                let savedStores = await db.getItem('baseball_hub_stores');
                let savedEvents = await db.getItem('baseball_hub_events');
                let savedAttendance = await db.getItem('baseball_hub_attendance');
                let savedVendors = await db.getItem('baseball_hub_vendors');
                let savedAdmins = await db.getItem('baseball_hub_admins');

                try {
                    const [storesRes, historyRes, vendorsRes, adminsRes] = await Promise.all([
                        fetchWithTimeout(`${API_BASE_URL}/stores`, {}, 60000),
                        fetchWithTimeout(`${API_BASE_URL}/history`, {}, 60000),
                        fetchWithTimeout(`${API_BASE_URL}/vendors`, {}, 60000),
                        fetchWithTimeout(`${API_BASE_URL}/admins`, {}, 60000)
                    ]);
 
                    if (storesRes.ok) {
                        const storesData = await storesRes.json();
                        if (storesData && storesData.stores) {
                            serverStores = storesData.stores;
                            // [PURGE] 서버 주도형 데이터 정화 (PC/모바일 공통)
                            const serverPurgeVersion = storesData.purgeVersion;
                            const localPurgeVersion = localStorage.getItem('baseball_hub_purge_version');
                            
                            if (serverPurgeVersion && serverPurgeVersion !== localPurgeVersion) {
                                console.warn(`%c[PURGE] New purge version detected: ${serverPurgeVersion}. Clearing local March 2026 data.`, "color: #ef4444; font-weight: bold;");
                                
                                // 모바일 등 강력한 로컬 캐싱 대응: IndexedDB 전체 및 localStorage 주요 키 강제 삭제
                                try {
                                    await db.clearAll();
                                    const legacyKeys = ['baseball_hub_stores', 'baseball_hub_events', 'baseball_hub_attendance', 'baseball_hub_vendors', 'baseball_hub_admins', 'baseball_hub_backups'];
                                    legacyKeys.forEach(k => localStorage.removeItem(k));
                                    console.log('[PURGE] Local IndexedDB and localStorage fully wiped.');
                                } catch (err) {
                                    console.error('[PURGE] Failed to wipe local DB:', err);
                                }

                                // 로컬(IndexedDB/localStorage)에서 2026년 전체 데이터 강제 제거
                                const forcePurgeData = (storeList) => {
                                    return storeList.map(s => {
                                        const updated = { ...s };
                                        if (updated.salesHistory) {
                                            Object.keys(updated.salesHistory).forEach(d => {
                                                if (d.startsWith('2026')) delete updated.salesHistory[d];
                                            });
                                        }
                                        if (updated.salesData) {
                                            updated.salesData = updated.salesData.filter(v => {
                                                const d = v.date || v.localDate || '';
                                                return !d.startsWith('2026');
                                            });
                                        }
                                        if (updated.dailyStatus) {
                                            Object.keys(updated.dailyStatus).forEach(d => {
                                                if (d.startsWith('2026')) delete updated.dailyStatus[d];
                                            });
                                        }
                                        // 추가: 재고, 시설, 발주 2026년 데이터도 삭제
                                        if (updated.inventoryHistory) {
                                            Object.keys(updated.inventoryHistory).forEach(d => {
                                                if (d.startsWith('2026')) delete updated.inventoryHistory[d];
                                            });
                                        }
                                        if (updated.facilityHistory) {
                                            Object.keys(updated.facilityHistory).forEach(d => {
                                                if (d.startsWith('2026')) delete updated.facilityHistory[d];
                                            });
                                        }
                                        if (updated.orderHistory) {
                                            Object.keys(updated.orderHistory).forEach(d => {
                                                if (d.startsWith('2026')) delete updated.orderHistory[d];
                                            });
                                        }
                                        if (updated.inventoryData && updated.inventoryData.stocks) {
                                            updated.inventoryData.stocks = [];
                                        }
                                        return updated;
                                    });
                                };

                                if (savedStores) savedStores = forcePurgeData(savedStores);
                                if (serverStores) serverStores = forcePurgeData(serverStores);
                                
                                localStorage.setItem('baseball_hub_purge_version', serverPurgeVersion);
                                // 정화 시에는 "로컬이 더 많으니 복구하겠다"는 로직 무효화
                                localStorage.setItem('purge_in_progress', 'true');
                            }
                        } else {
                            serverStores = storesData;
                        }
                    }
                    if (historyRes.ok) serverHistory = await historyRes.json();
                    if (vendorsRes.ok) serverVendors = await vendorsRes.json();
                    if (adminsRes.ok) {
                        const serverAdmins = await adminsRes.json();
                        setAdmins(serverAdmins);
                        await db.setItem('baseball_hub_admins', serverAdmins);
                    }
                    
                    // 신규 백업 데이터 서버 연동 로드
                    try {
                        const backupsRes = await fetchWithTimeout(`${API_BASE_URL}/backups`);
                        if (backupsRes.ok) {
                            const serverBackups = await backupsRes.json();
                            if (Array.isArray(serverBackups) && serverBackups.length > 0) {
                                // 로컬과 서버 백업 데이터가 충돌할 수 있으니 병합 또는 서버 데이터 사용
                                // 최신 10개를 유지하는 방식으로 구현
                                setBackups(serverBackups.slice(0, 10));
                                await db.setItem('baseball_hub_backups', serverBackups.slice(0, 10));
                            }
                        }
                    } catch(e) { console.warn('서버 백업 데이터 로드 실패', e); }
 
                    console.log('Data synced from server successfully');
                } catch (e) {
                    console.warn('Server sync failed or timed out, falling back to local storage:', e);
                }

                // 1-2. 로컬(IndexedDB) 데이터 로딩 프로세스 (상단 1-1a로 이동 완료)

                // 1-2a. 만약 IndexedDB에서 관리자 목록을 못 가져왔고 현재 비어있다면, 시스템 마비 방지를 위한 하드코딩 Fallback
                if (!savedAdmins || savedAdmins.length === 0) {
                    const fallbackAdmins = [
                        { id: 'admin', password: '3861', name: 'master', permission: 'full' },
                        { id: 'admin1', password: '9999', name: '관리자1', permission: 'full' },
                        { id: 'admin2', password: '0000', name: '관리자2', permission: 'full' },
                        { id: 'admin3', password: '0000', name: '관리자3', permission: 'full' },
                        { id: 'admin4', password: '0000', name: '관리자4', permission: 'full' }
                    ];
                    setAdmins(fallbackAdmins);
                    console.warn('[Sync] Admin list missing or wiped. Fallback admins injected to prevent login lock-out.');
                } else {
                    setAdmins(savedAdmins);
                }

                // 1-2a. 만약 IndexedDB가 비어있다면 legacy localStorage에서 시도 (최종 수단)
                if (!savedStores) {
                    try {
                        const legacyStores = localStorage.getItem('baseball_hub_stores');
                        if (legacyStores) {
                            savedStores = JSON.parse(legacyStores);
                            console.log('Legacy data found in localStorage and migrated');
                            // 마이그레이션 실행
                            await db.setItem('baseball_hub_stores', savedStores);
                            const legacyEvents = localStorage.getItem('baseball_hub_events');
                            if (legacyEvents) {
                                savedEvents = JSON.parse(legacyEvents);
                                await db.setItem('baseball_hub_events', savedEvents);
                            }
                        }
                    } catch (e) {
                        console.warn('Failed to parse legacy localStorage data', e);
                    }
                }

                // 1-3. 데이터 병합 로직 정교화 (기록 수 기반 자동 복구)
                const getRecordCount = (stores) => {
                    if (!stores || !Array.isArray(stores)) return 0;
                    return stores.reduce((acc, s) => {
                        const salesCount = Array.isArray(s.salesData) ? s.salesData.length : 0;
                        const historyCount = s.salesHistory ? Object.keys(s.salesHistory).length : 0;
                        return acc + salesCount + historyCount;
                    }, 0);
                };

                const serverRecords = getRecordCount(serverStores);
                const localRecords = getRecordCount(savedStores);
                const serverEventCount = serverHistory?.events ? Object.keys(serverHistory.events).length : 0;
                const localEventCount = savedEvents ? Object.keys(savedEvents).length : 0;

                setSyncStats({
                    serverRecords: serverRecords || 0,
                    localRecords: localRecords || 0
                });

                console.log(`[Sync] Data check - Server: ${serverRecords} recs, Local: ${localRecords} recs`);

                let finalStores = [];
                let usingDefaultData = false;
                const isPurging = localStorage.getItem('purge_in_progress') === 'true';

                // [수정] 서버 데이터가 존재하고 정상적이면 서버 데이터를 최우선적으로 신뢰합니다.
                // 이전의 'localRecords > serverRecords' 기반 자동 복구 로직은 의도적인 삭제/정화 시 오류를 유발하여 제거함.
                if (serverStores && Array.isArray(serverStores) && serverStores.length > 0) {
                    finalStores = serverStores;
                    if (isPurging) {
                        console.log('[Purge] Force overwriting local cache with clean server data');
                        localStorage.removeItem('purge_in_progress');
                    } else {
                        console.log('[Sync] Using server data');
                    }

                    // 디버깅용 로그 추가
                    if (!isPurging && localRecords > serverRecords) {
                        console.warn(`[Sync] Local has more records (${localRecords}) than server (${serverRecords}), but server data was selected.`);
                    }
                } else if (savedStores && savedStores.length > 0) {
                    finalStores = savedStores;
                    console.log('[Sync] Using local fallback (Offline mode)');
                } else {
                    finalStores = [];
                }

                const finalEvents = (serverHistory?.events !== undefined) ? (serverHistory.events) : (savedEvents || {});
                const finalAttendance = (serverHistory?.attendance !== undefined)
                    ? serverHistory.attendance
                    : (savedAttendance || {});
                const finalVendors = (serverVendors !== undefined) ? serverVendors : (savedVendors || []);

                // 초기 데이터가 아예 없는 경우 기본값 생성
                if (finalStores.length === 0) {
                    usingDefaultData = true;
                    finalStores = Array.from({ length: 50 }, (_, i) => ({
                        id: (i + 1).toString(),
                        name: `매장 ${i + 1}`,
                        items: ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'],
                        managerId: `store${i + 1}`,
                        password: '1234',
                        operatingType: 'direct', // 직영(direct) / 임대(rental)
                        salesData: [],
                        salesHistory: {},
                        inventoryData: { stocks: Array(7).fill(0) },
                        inventoryHistory: {},
                        facilityData: {
                            items: [
                                { name: 'POS/결제기기', status: 'normal', note: '' },
                                { name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
                                { name: '냉장/냉동고', status: 'normal', note: '' },
                                { name: '조명/전기시설', status: 'normal', note: '' },
                                { name: '기타 1', status: 'normal', note: '' },
                                { name: '기타 2', status: 'normal', note: '' },
                                { name: '기타 3', status: 'normal', note: '' }
                            ]
                        },
                        facilityHistory: {},
                        orderData: { stocks: Array(7).fill(0) },
                        orderHistory: {}
                    }));
                } else {
                    // 데이터 정규화 로직 (기존 코드 유지)
                    finalStores = finalStores.map(store => {
                        const updatedId = (store.id || '0').toString();
                        let updated = { ...store, id: updatedId };
                        if (!updated.items) updated.items = ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'];
                        if (!updated.inventoryData) updated.inventoryData = { stocks: Array(updated.items.length).fill(0) };
                        if (!updated.managerId) updated.managerId = `store${updatedId}`;
                        if (!updated.password) updated.password = '1234';
                        if (!updated.operatingType) updated.operatingType = 'direct';
                        if (!updated.inventoryHistory) updated.inventoryHistory = {};
                        if (!updated.salesHistory) updated.salesHistory = {};
                        if (!updated.facilityHistory) updated.facilityHistory = {};
                        if (!updated.orderHistory) updated.orderHistory = {};
                        if (!updated.dailyStatus) updated.dailyStatus = {};
                        if (!Array.isArray(updated.salesData)) updated.salesData = [];

                        return updated;
                    });
                }

                if (usingDefaultData) {
                    console.warn('[Sync] Default store data generated. Auto-sync to server is strictly blocked.');
                    isInitialLoad.current = true; // 서버로의 자동 동기화 차단
                }

                setStores(finalStores);
                setEventHistory(finalEvents);
                setAttendanceHistory(finalAttendance);
                setVendors(finalVendors);

                // 로컬 백업 데이터 로드 (서버에서 가져오지 못했을 때를 위한 대비)
                let savedBackups = await db.getItem('baseball_hub_backups');
                if (!savedBackups) {
                    savedBackups = localStorage.getItem('baseball_hub_backups');
                    if (savedBackups) savedBackups = JSON.parse(savedBackups);
                }
                
                // setBackups 호출은 이미 위에서 서버 백업을 성공적으로 가져온 경우 덮어쓰지 않도록 주의
                setBackups(prev => prev.length > 0 ? prev : (savedBackups || []));

                // 새로고침 시에는 유지되고, 브라우저/앱 종료 시에는 로그아웃되는 세션 방식 적용
                const savedSession = sessionStorage.getItem('baseball_hub_user');
                if (savedSession) {
                    try {
                        setUser(JSON.parse(savedSession));
                    } catch (e) {
                        setUser(null);
                    }
                } else {
                    setUser(null);
                }

                // [중요] localStorage 용량 부족(QuoteExceededError) 해결을 위한 클린업
                // IndexedDB에 이미 데이터가 있으므로, localStorage의 무거운 데이터들 삭제
                const heavyKeys = ['baseball_hub_stores', 'baseball_hub_backups', 'baseball_hub_events', 'baseball_hub_vendors'];
                heavyKeys.forEach(key => {
                    if (localStorage.getItem(key)) {
                        console.log(`[Cleanup] Removing heavy key from localStorage: ${key}`);
                        localStorage.removeItem(key);
                    }
                });

            } catch (error) {
                console.error('Failed to initialize data:', error);
                window.lastInitError = error.message;
                console.error('데이터 초기화 중 오류가 발생했습니다: ' + error.message);
            } finally {
                setIsLoading(false);
            }
        };

        initData();
    }, []);

    // 초기 로딩 완료 시점 파악 (동기화 방지용)
    useEffect(() => {
        if (!isLoading) {
            // 데이터 로딩이 완료된 후 약간의 지연을 주어 Effect들이 먼저 실행되지 않도록 함
            const timer = setTimeout(() => {
                isInitialLoad.current = false;
                console.log('[Sync] Initial load complete. Auto-sync enabled.');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    const createBackup = useCallback(async (backupType = 'manual') => {
        try {
            // [최적화] 거대한 데이터를 직접 보내지 않고 서버에서 직접 파일 복사 방식을 사용하도록 요청
            const response = await fetchWithTimeout(`${API_BASE_URL}/backups/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    note: backupType === 'manual' ? '사용자 수동 백업' : `자동 백업 (${backupType})`,
                    type: backupType 
                })
            }, 30000); // 파일 복사 시간이 걸릴 수 있으므로 타임아웃 30초로 증대

            const result = await response.json();
            
            if (result.success) {
                // 성공 시 서버로부터 받은 메타데이터를 로컬 상태에 반영 (전체 데이터는 제외됨)
                const newBackup = {
                    id: result.backupId,
                    type: backupType,
                    createdAt: result.backupId,
                    note: backupType === 'manual' ? '사용자 수동 백업' : `자동 백업 (${backupType})`
                };
                
                setBackups(prev => [newBackup, ...prev].slice(0, 30));
                return result.backupId;
            } else {
                throw new Error(result.error || '백업 생성 실패');
            }
        } catch (e) {
            console.error('서버 백업 생성 실패:', e);
            // 백업에 실패하더라도 메인 작업이 중단되지 않도록 함
            return null;
        }
    }, []);


    // 정기 자동 백업 로직은 서버(server.js)로 이관되었습니다.
    // 기존 프론트엔드 예약 작업(useEffect)을 안전하게 제거했습니다.

    // 서버로 데이터 전송하는 공통 함수
    const syncWithServer = async (endpoint, data) => {
        try {
            await fetchWithTimeout(`${API_BASE_URL}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }, 5000); // 전송은 5초 타임아웃
        } catch (e) {
            console.error(`Failed to sync ${endpoint} with server:`, e);
        }
    };

    // 2. 데이터 변경 시 서버 및 로컬 저장소 동기화 (IndexedDB 우선)
    useEffect(() => {
        const syncStores = async () => {
            if (!isLoading && !isInitialLoad.current && !isClearingData.current && stores && Array.isArray(stores)) {
                try {
                    await db.setItem('baseball_hub_stores', stores);
                } catch (e) {
                    console.error('Failed to save stores to local storage:', e);
                }
                syncWithServer('stores', stores);
                if (user && ['admin1', 'admin2', 'admin3', 'admin4'].includes(user.id)) {
                    logActivity('update', '매장 정보/매출 데이터 업데이트');
                }
            }
        };
        syncStores();
    }, [stores, isLoading]);

    useEffect(() => {
        if (!isLoading && !isInitialLoad.current && !isClearingData.current) {
            try {
                db.setItem('baseball_hub_events', eventHistory);
                syncWithServer('events', eventHistory);
                if (user && ['admin1', 'admin2', 'admin3', 'admin4'].includes(user.id)) {
                    logActivity('update', '공통 행사 정보 업데이트');
                }
            } catch (e) {
                console.error('Failed to save events:', e);
            }
        }
    }, [eventHistory, isLoading]);

    useEffect(() => {
        if (!isLoading && !isInitialLoad.current && !isClearingData.current) {
            try {
                db.setItem('baseball_hub_attendance', attendanceHistory);
                syncWithServer('attendance', attendanceHistory);
                if (user && ['admin1', 'admin2', 'admin3', 'admin4'].includes(user.id)) {
                    logActivity('update', '입장객 정보 업데이트');
                }
            } catch (e) {
                console.error('Failed to save attendance:', e);
            }
        }
    }, [attendanceHistory, isLoading]);

    useEffect(() => {
        if (!isLoading && !isInitialLoad.current && !isClearingData.current) {
            try {
                db.setItem('baseball_hub_vendors', vendors);
                syncWithServer('vendors', vendors);
                if (user && ['admin1', 'admin2', 'admin3', 'admin4'].includes(user.id)) {
                    logActivity('update', '거래처 정보 업데이트');
                }
            } catch (e) {
                console.error('Failed to save vendors:', e);
            }
        }
    }, [vendors, isLoading]);

    useEffect(() => {
        if (!isLoading && backups) {
            try {
                // 이전 로컬 전용 방식 보존 (원상복구 시 사용)
                // db.setItem('baseball_hub_backups', backups);
                // localStorage.setItem('baseball_hub_backups', JSON.stringify(backups)); // 용량 제한으로 제거

                // 변경된 방식: 로컬 캐시 + 서버 동기화
                db.setItem('baseball_hub_backups', backups);
                // 서버 동기화는 createBackup, deleteBackup 함수 내에서 직접 처리함
            } catch (e) {
                console.error('Failed to save backups:', e);
            }
        }
    }, [backups, isLoading]);

    useEffect(() => {
        if (!isLoading) {
            try {
                if (user) {
                    // IndexedDB와 localStorage는 영구 저장이므로 보안상 필요한 경우에만 사용
                    // 여기서는 새로고침 대응을 위해 sessionStorage를 주력으로 사용
                    sessionStorage.setItem('baseball_hub_user', JSON.stringify(user));
                    db.setItem('baseball_hub_user', user);
                } else {
                    sessionStorage.removeItem('baseball_hub_user');
                    db.removeItem('baseball_hub_user');
                    localStorage.removeItem('baseball_hub_user');
                }
            } catch (e) {
                console.error('Failed to save user session:', e);
            }
        }
    }, [user, isLoading]);

    const updateEvent = useCallback((date, eventName) => {
        setEventHistory(prev => ({ ...prev, [date]: eventName }));
    }, []);

    const updateAttendance = useCallback((date, count) => {
        setAttendanceHistory(prev => ({ ...prev, [date]: count }));
    }, []);

    const login = useCallback((userData) => setUser(userData), []);
    const logout = useCallback(async () => {
        await logActivity('logout', '로그아웃');

        // [신규] admin1 ~ admin4 명시적 로그아웃 시 서버 세션 해제
        if (user && ['admin1', 'admin2', 'admin3', 'admin4'].includes(user.id)) {
            try {
                const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                    ? 'http://localhost:3001/api' 
                    : `${window.location.origin}/api`;
                    
                await fetch(`${baseUrl}/logout-session`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: user.id })
                });
            } catch (err) {
                console.error('Failed to clear session on logout:', err);
            }
        }

        // 명시적으로 로컬 스토리지 데이터 등 확실하게 정리 후 리다이렉션을 위한 대기
        try {
            sessionStorage.removeItem('baseball_hub_user');
            localStorage.removeItem('baseball_hub_user');
            await db.removeItem('baseball_hub_user');
        } catch (e) {
            console.error('Failed to clear local user data on logout:', e);
        }

        setUser(null);
    }, [logActivity, user]);

    const updateStore = useCallback((id, newData) => {
        setStores(prev => {
            if (!prev) return prev;
            return prev.map(store => store.id == id ? { ...store, ...newData } : store);
        });
    }, []);

    const updateAllStores = useCallback((config) => {
        setStores(prev => {
            if (!prev) return prev;
            return prev.map(store => ({ ...store, ...config }));
        });
    }, []);

    const updateVendors = useCallback((newVendors) => setVendors(newVendors), []);

    const getStore = useCallback((id) => {
        if (!stores) return null;
        return stores.find(s => s.id == id);
    }, [stores]);

    const forceRestoreFromBackup = useCallback(async () => {
        setIsLoading(true);
        console.warn('EMERGENCY RESTORE STARTED');
        try {
            // 1. 모든 알려진 키의 localStorage 확인
            const keys = ['baseball_hub_stores', 'baseball_hub_events', 'baseball_hub_attendance', 'baseball_hub_vendors'];
            let foundData = false;

            for (const key of keys) {
                const data = localStorage.getItem(key);
                if (data) {
                    const parsed = JSON.parse(data);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        console.log(`Found recovery data in localStorage: ${key}`);
                        await db.setItem(key, parsed);
                        foundData = true;
                    }
                }
            }

            // 2. 서버로 현재 로컬 상태 강제 전송
            const currentStores = await db.getItem('baseball_hub_stores');
            if (currentStores && currentStores.length > 0) {
                await syncWithServer('stores', currentStores);
                setStores(currentStores);
                foundData = true;
            }

            if (foundData) {
                console.warn('데이터 복구 알림: 일부 데이터가 로컬에서 발견되어 서버로 복구되었습니다. 페이지를 새로고침합니다.');
                window.location.reload();
            } else {
                console.warn('데이터 복구 알림: 로컬 저장소에서도 유효한 과거 데이터를 찾지 못했습니다. 다른 브라우저나 기기를 확인해 보세요.');
            }
        } catch (e) {
            console.error('Recovery failed:', e);
        } finally {
            setIsLoading(false);
        }
    }, [syncWithServer]);

    // 백업/복구 함수들


    const restoreFromBackup = useCallback(async (backupId) => {
        const backup = backups.find(b => b.id === backupId);
        if (!backup) {
            throw new Error('백업을 찾을 수 없습니다.');
        }

        // [신규] 메타데이터 모델 백업(data 속성 없음) 처리
        if (!backup.data) {
            // 이 신규 형식의 백업은 서버 파일 복구(/api/backups/restore)를 이용해야 함을 알림
            throw new Error('이 백업은 최적화된 서버 측 백업입니다. 하단의 "서버 파일 백업 리스트"에서 복원을 진행해 주세요.');
        }

        setStores(backup.data.stores);
        setVendors(backup.data.vendors);
        setEventHistory(backup.data.eventHistory || {});
        setAttendanceHistory(backup.data.attendanceHistory || {});

        // 로컬 및 서버 동기화
        await db.setItem('baseball_hub_stores', backup.data.stores);
        await db.setItem('baseball_hub_vendors', backup.data.vendors);
        await db.setItem('baseball_hub_events', backup.data.eventHistory || {});
        await db.setItem('baseball_hub_attendance', backup.data.attendanceHistory || {});

        await syncWithServer('stores', backup.data.stores);
        await syncWithServer('vendors', backup.data.vendors);
        await syncWithServer('events', backup.data.eventHistory || {});
        await syncWithServer('attendance', backup.data.attendanceHistory || {});
    }, [backups, syncWithServer]);

    const deleteBackup = useCallback((backupId) => {
        setBackups(prev => {
            const updatedBackups = prev.filter(b => b.id !== backupId);
            
            // 삭제된 후의 배열을 서버로 전파하여 일치시킴
            fetchWithTimeout(`${API_BASE_URL}/backups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedBackups)
            }, 5000).catch(e => console.error('서버 백업 삭제 전파 실패', e));
            
            return updatedBackups;
        });
    }, []);

    // 데이터 초기화 함수들 (Analytics 페이지에서 사용)
    const clearVendorsData = useCallback(async () => {
        isClearingData.current = true;
        await createBackup('auto_before_vendor_clear');
        setVendors([]);
        await db.setItem('baseball_hub_vendors', []);
        await syncWithServer('vendors', []);
        setTimeout(() => { isClearingData.current = false; }, 1000);
    }, [syncWithServer, createBackup]);

    const clearSalesData = useCallback(async () => {
        isClearingData.current = true;
        await createBackup('auto_before_sales_clear');
        const updatedStores = stores.map(store => ({
            ...store,
            salesData: [],
            salesHistory: {},
            dailyStatus: Object.fromEntries(
                Object.entries(store.dailyStatus || {}).map(([date, status]) => [
                    date,
                    { ...status, sales: false }
                ])
            )
        }));
        setStores(updatedStores);
        await db.setItem('baseball_hub_stores', updatedStores);
        await syncWithServer('stores', { stores: updatedStores, merge: false });
        setTimeout(() => { isClearingData.current = false; }, 1000);
    }, [stores, createBackup, syncWithServer]);

    const clearInventoryData = useCallback(async () => {
        isClearingData.current = true;
        await createBackup('auto_before_inventory_clear');
        const updatedStores = stores.map(store => ({
            ...store,
            inventoryData: { stocks: Array(store.items.length).fill(0) },
            inventoryHistory: {},
            dailyStatus: Object.fromEntries(
                Object.entries(store.dailyStatus || {}).map(([date, status]) => [
                    date,
                    { ...status, inventory: false }
                ])
            )
        }));
        setStores(updatedStores);
        await db.setItem('baseball_hub_stores', updatedStores);
        await syncWithServer('stores', { stores: updatedStores, merge: false });
        setTimeout(() => { isClearingData.current = false; }, 1000);
    }, [stores, createBackup, syncWithServer]);

    const clearFacilityData = useCallback(async () => {
        isClearingData.current = true;
        await createBackup('auto_before_facility_clear');
        const updatedStores = stores.map(store => ({
            ...store,
            facilityData: {
                items: [
                    { name: 'POS/결제기기', status: 'normal', note: '' },
                    { name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
                    { name: '냉장/냉동고', status: 'normal', note: '' },
                    { name: '조명/전기시설', status: 'normal', note: '' },
                    { name: '기타 1', status: 'normal', note: '' },
                    { name: '기타 2', status: 'normal', note: '' },
                    { name: '기타 3', status: 'normal', note: '' }
                ]
            },
            facilityHistory: {},
            dailyStatus: Object.fromEntries(
                Object.entries(store.dailyStatus || {}).map(([date, status]) => [
                    date,
                    { ...status, facility: false }
                ])
            )
        }));
        setStores(updatedStores);
        await db.setItem('baseball_hub_stores', updatedStores);
        await syncWithServer('stores', { stores: updatedStores, merge: false });
        setTimeout(() => { isClearingData.current = false; }, 1000);
    }, [stores, createBackup, syncWithServer]);

    const clearOrderData = useCallback(async () => {
        isClearingData.current = true;
        await createBackup('auto_before_order_clear');
        const updatedStores = stores.map(store => ({
            ...store,
            orderData: { stocks: Array(store.items.length).fill(0) },
            orderHistory: {},
            dailyStatus: Object.fromEntries(
                Object.entries(store.dailyStatus || {}).map(([date, status]) => [
                    date,
                    { ...status, order: false }
                ])
            )
        }));
        setStores(updatedStores);
        await db.setItem('baseball_hub_stores', updatedStores);
        await syncWithServer('stores', { stores: updatedStores, merge: false });
        setTimeout(() => { isClearingData.current = false; }, 1000);
    }, [stores, createBackup, syncWithServer]);

    const clearAllData = useCallback(async () => {
        try {
            isClearingData.current = true;
            setIsLoading(true);
            await createBackup('auto_before_all_clear');
            
            // 1. 거래처 초기화
            await clearVendorsData();

            // 2. 공통 행사 및 입장객 통계 초기화
            setEventHistory({});
            setAttendanceHistory({});
            await db.setItem('baseball_hub_events', {});
            await db.setItem('baseball_hub_attendance', {});
            await syncWithServer('events', { data: {}, merge: false });
            await syncWithServer('attendance', { data: {}, merge: false });

            // 3. 매장 데이터 일괄 초기화
            const updatedStores = stores.map(store => ({
                ...store,
                salesData: [],
                salesHistory: {},
                inventoryData: { stocks: Array(store.items?.length || 7).fill(0) },
                inventoryHistory: {},
                facilityData: {
                    items: [
                        { name: 'POS/결제기기', status: 'normal', note: '' },
                        { name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
                        { name: '냉장/냉동고', status: 'normal', note: '' },
                        { name: '조명/전기시설', status: 'normal', note: '' },
                        { name: '기타 1', status: 'normal', note: '' },
                        { name: '기타 2', status: 'normal', note: '' },
                        { name: '기타 3', status: 'normal', note: '' }
                    ]
                },
                facilityHistory: {},
                orderData: { stocks: Array(store.items?.length || 7).fill(0) },
                orderHistory: {},
                dailyStatus: {}
            }));
            
            setStores(updatedStores);
            await db.setItem('baseball_hub_stores', updatedStores);
            await syncWithServer('stores', { stores: updatedStores, merge: false });

            // [핵심] 모든 동기화가 끝난 후 로컬 저장소를 완전히 비워 레이스 컨디션 차단
            console.log('초기화 완료: 로컬 저장소 정리 및 페이지 새로고침을 실행합니다.');
            
            // IndexedDB의 주요 키들을 모두 삭제
            const keysToDelete = [
                'baseball_hub_stores', 
                'baseball_hub_events', 
                'baseball_hub_attendance', 
                'baseball_hub_vendors',
                'baseball_hub_backups'
            ];
            for (const key of keysToDelete) {
                await db.removeItem(key);
            }

            // localStorage도 클리어
            localStorage.clear();

            // 브라우저 캐시를 무시하고 페이지를 완전히 새로고침하여 서버에서 깨끗한 데이터를 받아오도록 함
            window.location.href = window.location.pathname + '?cleared=' + Date.now();
        } catch (error) {
            console.error('전체 데이터 초기화 중 에러 발생:', error);
            alert('초기화 중 오류가 발생했습니다. 페이지를 새로고침 후 다시 시도해 주세요.');
        } finally {
            setIsLoading(false);
        }
    }, [stores, clearVendorsData, createBackup, syncWithServer]);



    // 비상 복구 함수를 전역에 노출 (Login 페이지에서 사용)
    useEffect(() => {
        window.forceRestore = forceRestoreFromBackup;
    }, [forceRestoreFromBackup]);

    if (isLoading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#0f172a',
                color: '#38bdf8',
                fontSize: '1.2rem',
                fontWeight: '600',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{ marginBottom: '20px' }}>데이터 동기화 중입니다...</div>
                
                {/* 에러 발생 시 상세 메시지 표시 */}
                {window.lastInitError && (
                    <div style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '20px', fontWeight: '400', maxWidth: '300px', wordBreak: 'break-all' }}>
                        오류 발생: {window.lastInitError}
                    </div>
                )}

                {/* 무한 로딩 또는 오류 시 강제 초기화 버튼 */}
                <button 
                    onClick={() => {
                        if (window.confirm('모든 로컬 데이터를 초기화하고 다시 로그인하시겠습니까? (서버 데이터는 유지됩니다)')) {
                            localStorage.clear();
                            indexedDB.deleteDatabase('baseball_hub_db'); 
                            window.location.reload();
                        }
                    }}
                    style={{
                        padding: '10px 20px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid #ef4444',
                        color: '#ef4444',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        marginTop: '10px'
                    }}
                >
                    화면이 멈췄을 때: 초기화 후 재시도
                </button>

                <div style={{ marginTop: '20px', fontSize: '0.7rem', color: '#64748b' }}>
                    접속 주소: {window.location.href}
                </div>
            </div>
        );
    }

    return (
        <StoreContext.Provider value={{
            stores,
            updateStore,
            updateAllStores,
            getStore,
            forceRestoreFromBackup,
            clearVendorsData,
            clearSalesData,
            clearInventoryData,
            clearFacilityData,
            clearOrderData,
            clearAllData,
            backups,
            createBackup,
            restoreFromBackup,
            deleteBackup,
            user,
            login,
            logout,
            logActivity,
            currentDate,
            getLocalISODate,
            isLoading,
            eventHistory,
            updateEvent,
            attendanceHistory,
            updateAttendance,
            vendors,
            updateVendors,
            admins,
            selectedStoreId,
            setSelectedStoreId,
            debugInfo: {
                serverRecords: syncStats.serverRecords,
                localRecords: syncStats.localRecords,
                totalStores: stores.length,
                recordsWithData: stores.filter(s => (s.salesData?.length > 0) || (Object.keys(s.salesHistory || {}).length > 0)).length
            }
        }}>
            {children}
        </StoreContext.Provider>
    );
};
